import React, { useState } from 'react';
import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  DashboardIcon, CalendarIcon, NavTicketIcon, WalletIcon,
  MapIcon, TrophyIcon, ScanIcon, DiamondIcon, NavUsersIcon, LogsIcon, BingoIcon, ClockIcon,
  NewsletterIcon, CateringIcon, MonitorLinkIcon, InfoIcon,
} from '../../components/Icons';

const allNavItems = [
  { to: '/admin',             label: 'Dashboard',  Icon: DashboardIcon,  end: true,  roles: ['admin', 'superadmin'] },
  { to: '/admin/events',      label: 'Events',     Icon: CalendarIcon,               roles: ['admin', 'superadmin'] },
  { to: '/admin/timetable',   label: 'Timetable',  Icon: ClockIcon,                  roles: ['admin', 'superadmin'] },
  { to: '/admin/tickets',     label: 'Tickets',    Icon: NavTicketIcon,              roles: ['admin', 'superadmin'] },
  { to: '/admin/payments',    label: 'Zahlungen',  Icon: WalletIcon,                 roles: ['admin', 'superadmin'] },
  { to: '/admin/newsletter',  label: 'Newsletter', Icon: NewsletterIcon,             roles: ['admin', 'superadmin'] },
  { to: '/admin/seats',       label: 'Sitzpläne',  Icon: MapIcon,                    roles: ['admin', 'superadmin'] },
  { to: '/admin/catering',    label: 'Catering',   Icon: CateringIcon,               roles: ['admin', 'superadmin'] },
  { to: '/admin/tournaments', label: 'Turniere',   Icon: TrophyIcon,                 roles: ['admin', 'superadmin', 'tournament_admin'] },
  { to: '/admin/checkin',     label: 'Check-in',   Icon: ScanIcon,                   roles: ['admin', 'superadmin'] },
  { to: '/admin/sponsors',    label: 'Sponsoren',  Icon: DiamondIcon,                roles: ['admin', 'superadmin'] },
  { to: '/admin/users',       label: 'Benutzer',   Icon: NavUsersIcon,               roles: ['admin', 'superadmin'] },
  { to: '/admin/logs',        label: 'Logs',       Icon: LogsIcon,                   roles: ['admin', 'superadmin'] },
  { to: '/admin/bingo',       label: 'Bingo',      Icon: BingoIcon,                  roles: ['admin', 'superadmin'] },
  { to: '/admin/info',        label: 'Infos',      Icon: InfoIcon,                   roles: ['admin', 'superadmin'] },
  { to: '/admin/links',       label: 'Anzeige-Links', Icon: MonitorLinkIcon,         roles: ['admin', 'superadmin', 'tournament_admin'] },
];

export default function AdminLayout() {
  const { isAnyAdmin, user, loading } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) return null;
  if (!isAnyAdmin) return <Navigate to="/" replace />;

  const navItems = allNavItems.filter(({ roles }) => roles.includes(user?.role));
  const defaultPath = user?.role === 'tournament_admin' ? '/admin/tournaments' : '/admin';

  const current = navItems.find(({ to, end }) =>
    end ? location.pathname === to : location.pathname.startsWith(to)
  );

  return (
    <div className="flex flex-col md:flex-row gap-0 md:gap-6 min-h-[calc(100vh-180px)]">

      {/* Mobile: Dropdown */}
      <div className="md:hidden mb-4">
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="w-full flex items-center justify-between bg-gradient-to-r from-ocean-800 to-ocean-900 text-white px-4 py-3 rounded-lg text-sm font-semibold shadow-sm"
        >
          <span className="flex items-center gap-2">
            {current?.Icon && <current.Icon className="w-4 h-4 opacity-75" />}
            {current?.label || 'Admin'}
          </span>
          <svg className={`w-4 h-4 transition-transform duration-200 ${mobileOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {mobileOpen && (
          <div className="mt-1 bg-white border border-slate-200 rounded-lg overflow-hidden shadow-lg divide-y divide-slate-100">
            {navItems.map(({ to, label, Icon, end }) => (
              <NavLink key={to} to={to} end={end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium no-underline transition-colors ${
                    isActive ? 'bg-ocean-50 text-ocean-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'
                  }`
                }>
                {({ isActive }) => (
                  <>
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-ocean-500' : 'text-slate-400'}`} />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        )}
      </div>

      {/* Desktop: Sidebar */}
      <aside className="hidden md:block w-48 flex-shrink-0">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden sticky top-28 shadow-card">
          <div className="bg-gradient-to-br from-ocean-800 to-ocean-950 px-4 py-3.5">
            <p className="text-[10px] font-bold text-ocean-300 uppercase tracking-widest">Administration</p>
          </div>
          <nav className="py-1">
            {navItems.map(({ to, label, Icon, end }) => (
              <NavLink key={to} to={to} end={end}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium transition-all duration-100 no-underline relative ${
                    isActive
                      ? 'bg-ocean-50 text-ocean-700'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-ocean-700'
                  }`
                }>
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-ocean-500 rounded-r-full" />
                    )}
                    <Icon className={`w-4 h-4 flex-shrink-0 transition-colors ${isActive ? 'text-ocean-500' : 'text-slate-400'}`} />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
